import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useUser } from '@/hooks';
import { useWizardStatus } from '@/hooks/useWizard';
import { FullPageLoader } from './ui/FullPageLoader';

/**
 * A component that renders its children only if the user is authenticated.
 * If not authenticated, it redirects to the login page.
 * For external users with incomplete wizard, redirects to /wizard.
 * Shows a loading state while authentication status is being verified.
 */
export default function PrivateRoute() {
  const { isLogged } = useAuth();
  const user = useUser();
  const location = useLocation();
  const { status: wizardStatus, loading: wizardLoading } = useWizardStatus();

  // Show loading state while checking authentication status
  if (isLogged === null || wizardLoading) {
    return <FullPageLoader message="Authenticating" />;
  }

  // Redirect to login if not authenticated
  if (!isLogged) {
    const redirectPath = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirectPath}`} replace />;
  }

  // For external users with incomplete wizard, redirect to wizard
  // (unless they're already on the wizard page)
  // Use wizardStatus from API (most up-to-date) instead of cookie-based user data
  if (
    user?.role === 'external' &&
    !wizardStatus?.wizardCompleted &&
    location.pathname !== '/wizard'
  ) {
    return <Navigate to="/wizard" replace />;
  }

  // Render the protected content
  return <Outlet />;
}
